import useUrlState from "@ahooksjs/use-url-state";
import clsx from "clsx";
import { endOfMonth, format, formatDate, isFuture, isSameMonth, startOfMonth } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import { Button } from "devextreme-react/button";
import { LoadPanel } from "devextreme-react/load-panel";
import { Pagination } from "devextreme-react/pagination";
import { TextBox } from "devextreme-react/text-box";
import { Toolbar, Item as ToolbarItem } from "devextreme-react/toolbar";
import { Tooltip } from "devextreme-react/tooltip";
import notify from "../../../lib/notify";
import {
  buildEmploymentIndex,
  EmploymentIndex,
  employmentKey,
  getEmploymentPeriods,
  isDayWithinEmployment,
} from "../../../lib/employment";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { sendCalculateWorkingTimeSummaryRequest } from "../../../api/calculateWorkingTimeSummaryJob";
import PresenceConfirmPopup from "../../../components/presence-confirm-popup/PresenceConfirmPopup";
import PresenceReportingPopup from "../../../components/presence-reporting-popup/PresenceReportingPopup";
import { useAuth } from "../../../contexts/auth";
import { useCompanyContext } from "../../../contexts/company";
import { useThemeContext } from "../../../theme/theme";
import {
  CalculateWorkingTimeSummaryResult,
  PresenceAvailableEmployee,
  PresenceAvailableProject,
  RoleEnum,
} from "../../../types";
import { formatNumber } from "../../../utils/intl";
import { useScreenSize } from "../../../utils/media-query";
import {
  exportPresenceXlsx,
  getPresence,
  getPresenceAvailableEmployees,
  getPresenceAvailableProjects,
} from "../api/Presence";
import { useViewSwitchContext } from "../contexts/ViewSwitchContext";
import { PresenceEntry, PresenceResponse } from "../types/Presence";
import {
  AbsenceHourRange,
  getAbsenceHourRange,
  isHourlyAbsence,
} from "../utils/absenceHelpers";
import CalculateWorkingTimeSummaryResultDialog from "./CalculateWorkingTimeSummaryResultDialog";
import CommissionHoursV2Popup from "./CommissionHoursV2Popup";

type CellMeta = {
  bgColor: string;
  firstLine: string | null;
  secondLine: string | null;
  icon: JSX.Element | null;
  redBorder: boolean;
  hourlyAbsenceLabel: string | null;
};

export const EmployeesPresence = () => {
  const { t, i18n } = useTranslation();
  const themeContext = useThemeContext();
  const authContext = useAuth();
  const companyContext = useCompanyContext();
  const { setViewState } = useViewSwitchContext();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const acceptedAtParams = (value: string) => {
    const date = new Date(value);
    const locale = i18n.language === "en" ? enUS : pl;
    return {
      date: format(date, "dd.MM.yyyy", { locale }),
      time: format(date, "HH:mm", { locale }),
    };
  };

  const isHeadAdministrator = authContext.user!.roles!.includes(
    RoleEnum.AdministratorGlowny,
  );
  const isHrOrAdmin =
    authContext.user!.roles!.includes(RoleEnum.KadrySpolki) ||
    authContext.user!.roles!.includes(RoleEnum.AdministratorSpolki) ||
    authContext.user!.roles!.includes(RoleEnum.AdministratorGlowny);
  const companyId =
    isHeadAdministrator && companyContext.companyId !== 0
      ? companyContext.companyId
      : undefined;

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const currentDate = new Date();
  const [urlState, setUrlState] = useUrlState({
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
    pageNumber: 1,
    pageSize: 10,
    search: "",
  });

  const [presenceResponse, setPresenceResponse] = useState<PresenceResponse>();
  // Przedziały zatrudnienia wyświetlonych pracowników - do wyszarzania kratek poza okresem umowy.
  const [employmentIndex, setEmploymentIndex] = useState<EmploymentIndex>(
    new Map(),
  );
  const [availableEmployees, setAvailableEmployees] = useState<
    PresenceAvailableEmployee[]
  >([]);
  const [availableProjects, setAvailableProjects] = useState<
    PresenceAvailableProject[]
  >([]);

  const [showPresenceConfirmPopup, setShowPresenceConfirmPopup] =
    useState(false);
  const [presenceReportingPopupData, setPresenceReportingPopupData] = useState<{
    companyId: number;
    employeeId: number;
    entry: PresenceEntry;
    employeeEntries: PresenceEntry[];
    absenceHourRange: AbsenceHourRange | null;
  }>();

  const [lastClickTime, setLastClickTime] = useState(0);
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(
    null,
  );

  const [
    showCalculateWorkingTimeSummaryResultsDialog,
    setShowCalculateWorkingTimeSummaryResultsDialog,
  ] = useState(false);
  const [
    isCalculateWorkingTimeSummaryProcessing,
    setIsCalculateWorkingTimeSummaryProcessing,
  ] = useState(false);
  const [
    calculateWorkingTimeSummaryResults,
    setCalculateWorkingTimeSummaryResults,
  ] = useState<CalculateWorkingTimeSummaryResult[]>();

  const [showCommissionHoursPopup, setShowCommissionHoursPopup] =
    useState(false);

  const [isExporting, setIsExporting] = useState(false);

  const getCellMeta = useCallback(
    (entry: PresenceEntry, isDark: boolean): CellMeta => {
      let bgColor = isDark ? "bg-[#383838]" : "bg-[#e9e9e9]";
      let firstLine: string | null = null;
      let secondLine: string | null = null;
      let icon: JSX.Element | null = null;
      let redBorder = false;
      let hourlyAbsenceLabel: string | null = null;

      const hourly = isHourlyAbsence(entry.absence);
      if (hourly && entry.absence !== null) {
        hourlyAbsenceLabel = `${entry.absence.acronym} ${entry.absence.hourFrom!.slice(
          0,
          5,
        )}-${entry.absence.hourTo!.slice(0, 5)}`;
      }

      if (entry.absence !== null && entry.absence.confirmed !== -1 && !hourly) {
        if (entry.absence.isLeave) {
          if (entry.absence.confirmed === 0 || entry.absence.confirmed === 1) {
            bgColor = "bg-[#4a5565]";
          } else {
            bgColor = "bg-[#1d4ed8]";
          }
        } else if (entry.absence.isSickLeave) {
          bgColor = "bg-[#ad4ae2]";
        } else {
          bgColor = "bg-[#1d4ed8]";
        }

        firstLine = entry.absence.acronym;

        if (entry.absence.synchronized !== null) {
          if (entry.absence.synchronized === true) {
            icon = <i className="dx-icon-check text-green-500"></i>;
          } else if (entry.absence.confirmed === 2) {
            icon = <i className="dx-icon-clock text-orange-400"></i>;
          } else {
            icon = <i className="dx-icon-close text-red-500"></i>;
          }
        }
      } else if (entry.presence !== null) {
        if (entry.presence.confirmed === null) {
          bgColor = "bg-emerald-400";
        } else if (entry.presence.confirmed === 0) {
          bgColor = "bg-amber-700";
        } else if (entry.presence.confirmed === 1) {
          bgColor = "bg-yellow-500";
        } else if (entry.presence.confirmed === 2) {
          bgColor = "bg-emerald-600";
        }

        firstLine = t("common.presenceAbbreviation", {
          value: entry.presence.label,
        });

        secondLine = t("common.workPlanAbbreviation", {
          value: entry.workPlan?.label ?? t("common.none"),
        });

        if (entry.presence.synchronized !== null) {
          if (entry.presence.synchronized === true) {
            icon = <i className="dx-icon-check text-green-500"></i>;
          } else if (entry.presence.confirmed === 2) {
            icon = <i className="dx-icon-clock text-orange-400"></i>;
          } else {
            icon = <i className="dx-icon-close text-red-500"></i>;
          }
        }
      } else {
        if (entry.workPlan?.dayType === 2) {
          bgColor = "bg-[#4d7c0f]";
          if (entry.workPlan.dayOffType !== null) {
            firstLine = entry.workPlan.dayOffType;
          }
        } else if (entry.workPlan?.dayType === 3) {
          bgColor = "bg-[#a3e635]";
          if (entry.workPlan.dayOffType !== null) {
            firstLine = entry.workPlan.dayOffType;
          }
        } else {
          firstLine = t("common.workPlanAbbreviation", {
            value: entry.workPlan?.label ?? t("common.none"),
          });
        }
      }

      if (entry.violations.length > 0) {
        redBorder = true;
      }

      return {
        bgColor,
        firstLine,
        secondLine,
        icon,
        redBorder,
        hourlyAbsenceLabel,
      };
    },
    [t],
  );

  const fetchPresences = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const response = await getPresence(
        urlState.year,
        urlState.month,
        companyId,
        urlState.pageNumber,
        urlState.pageSize,
        urlState.search,
      );
      setPresenceResponse(response);

      // przedziały zatrudnienia dla wyświetlonych pracowników - do blokady kratek poza umową
      const employees = response.data.map((d) => {
        const [cId, eId] = d.employee.id.split("-").map(Number);
        return { companyId: cId, employeeId: eId };
      });
      const employmentFrom = format(
        startOfMonth(new Date(urlState.year, urlState.month - 1, 1)),
        "yyyy-MM-dd",
      );
      const employmentTo = format(
        endOfMonth(new Date(urlState.year, urlState.month - 1, 1)),
        "yyyy-MM-dd",
      );
      try {
        const periods = await getEmploymentPeriods(
          employees,
          employmentFrom,
          employmentTo,
        );
        setEmploymentIndex(buildEmploymentIndex(periods));
      } catch {
        // fail-open: brak danych o zatrudnieniu nie blokuje edycji
        setEmploymentIndex(new Map());
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    companyId,
    urlState.month,
    urlState.pageNumber,
    urlState.pageSize,
    urlState.year,
    urlState.search,
    t,
  ]);

  const fetchAvailableEmployees = useCallback(async () => {
    try {
      const from = format(
        startOfMonth(new Date(urlState.year, urlState.month - 1, 1)),
        "yyyy-MM-dd",
      );
      const to = format(
        endOfMonth(new Date(urlState.year, urlState.month - 1, 1)),
        "yyyy-MM-dd",
      );
      setAvailableEmployees(
        await getPresenceAvailableEmployees(companyId, from, to),
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
      );
    }
  }, [companyId, urlState.year, urlState.month, t]);

  const fetchAvailableProjects = useCallback(async () => {
    try {
      setAvailableProjects(await getPresenceAvailableProjects(companyId));
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
      );
    }
  }, [companyId, t]);

  const handlePresenceConfirmPopupClose = useCallback(
    (changesHaveBeenMade: boolean) => {
      setShowPresenceConfirmPopup(false);

      if (changesHaveBeenMade) {
        fetchPresences();
      }
    },
    [fetchPresences],
  );

  const handlePresenceReportingPopupClose = useCallback(
    (presenceHasBeenCreatedOrUpdated: boolean) => {
      setPresenceReportingPopupData(undefined);

      if (presenceHasBeenCreatedOrUpdated) {
        notify(t("presence.PresenceSaveToast"), "success", 5000);
        fetchPresences();
      }
    },
    [fetchPresences, t],
  );

  const handlePreviousMonthButtonClick = useCallback(() => {
    const newValue = Number(urlState.month) - 1;
    if (newValue < 1) {
      setUrlState((s) => ({
        month: 12,
        year: Number(s.year) - 1,
        pageNumber: 1,
      }));
    } else {
      setUrlState({ month: newValue, pageNumber: 1 });
    }
  }, [setUrlState, urlState.month]);

  const handleNextMonthButtonClick = useCallback(() => {
    const newValue = Number(urlState.month) + 1;
    if (newValue > 12) {
      setUrlState((s) => ({
        month: 1,
        year: Number(s.year) + 1,
        pageNumber: 1,
      }));
    } else {
      setUrlState({ month: newValue, pageNumber: 1 });
    }
  }, [setUrlState, urlState.month]);

  const handleCreateCalculateWorkingTimeSummaryJob = useCallback(async () => {
    try {
      setIsCalculateWorkingTimeSummaryProcessing(true);

      const data = await sendCalculateWorkingTimeSummaryRequest(
        Number(urlState.year),
        Number(urlState.month),
        companyId,
      );

      setIsCalculateWorkingTimeSummaryProcessing(false);
      setCalculateWorkingTimeSummaryResults(data);
      setShowCalculateWorkingTimeSummaryResultsDialog(true);
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
        "error",
        5000,
      );
    }
  }, [companyId, t, urlState.month, urlState.year]);

  const handleExportXlsxClick = useCallback(async () => {
    try {
      setIsExporting(true);
      await exportPresenceXlsx(
        Number(urlState.year),
        Number(urlState.month),
        companyId,
      );
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : t("common.unknownErrorOccurred"),
        "error",
        5000,
      );
    } finally {
      setIsExporting(false);
    }
  }, [companyId, t, urlState.month, urlState.year]);

  const handleEntryClick = useCallback(
    (item: PresenceResponse["data"][number], entry: PresenceEntry) => {
      if (
        !(
          (entry.isLent === null && entry.isBorrowed === null) ||
          entry.isLent === false ||
          entry.isBorrowed === true
        )
      ) {
        return;
      }

      const entryDate = new Date(`${entry.date}T00:00:00`);

      // blokada dni poza okresem zatrudnienia w tej spółce (umowa do X / od Y)
      const [empCompanyId, empEmployeeId] = item.employee.id
        .split("-")
        .map(Number);
      if (
        !isDayWithinEmployment(
          employmentIndex.get(employmentKey(empCompanyId, empEmployeeId)),
          entry.date,
        )
      ) {
        notify(t("presence.OutsideEmployment"), "warning", 3000);
        return;
      }

      const allowFutureInCurrentMonth =
        authContext.user!.roles!.some((r) =>
          [
            RoleEnum.Majster.toString(),
            RoleEnum.Kierownik.toString(),
            RoleEnum.Manager.toString(),
            RoleEnum.KadrySpolki.toString(),
            RoleEnum.AdministratorSpolki.toString(),
            RoleEnum.AdministratorGlowny.toString(),
          ].includes(r),
        ) && isSameMonth(entryDate, new Date());

      if (isFuture(entry.date) && !allowFutureInCurrentMonth) {
        notify(t("presence.FutureAdd"), "warning", 3000);
        return;
      }

      const hourlyAbsence = isHourlyAbsence(entry.absence);
      if (
        entry.absence !== null &&
        entry.absence.confirmed !== -1 &&
        !hourlyAbsence
      ) {
        notify(t("presence.PopupBlock"), "warning", 3000);
        return;
      }

      if (entry.presence !== null && entry.presence.confirmed === null) {
        notify(t("presence.BlockedFromOptima"), "warning");
        return;
      }

      if (entry.presence !== null && entry.presence.confirmed === 2) {
        // Kadry/Admin mogą jeszcze cofnąć zatwierdzoną obecność, ale tylko jeśli jeszcze nie zsynchronizowana
        const canOverride =
          isHrOrAdmin && entry.presence.synchronized === false;
        if (!canOverride) {
          notify(t("presence.PresenceFullyConfirmed"), "warning", 3000);
          return;
        }
      }

      const [cId, eId] = item.employee.id.split("-").map(Number);
      const referenceDate = new Date(`${entry.date}T00:00:00`);
      setPresenceReportingPopupData({
        companyId: cId,
        employeeId: eId,
        entry,
        employeeEntries: item.entries,
        absenceHourRange: getAbsenceHourRange(entry.absence, referenceDate),
      });
    },
    [t, isHrOrAdmin, employmentIndex],
  );

  useEffect(() => {
    const isUserAuthorized =
      authContext.user!.roles!.some((role) =>
        [
          RoleEnum.Majster.toString(),
          RoleEnum.Kierownik.toString(),
          RoleEnum.Manager.toString(),
          RoleEnum.KadrySpolki.toString(),
          RoleEnum.AdministratorSpolki.toString(),
          RoleEnum.AdministratorGlowny.toString(),
        ].includes(role),
      ) || authContext.user!.isTimeManager;

    if (!isUserAuthorized) {
      setViewState("my");
    } else {
      fetchPresences();
    }
  }, [
    fetchPresences,
    urlState.year,
    urlState.month,
    urlState.pageNumber,
    urlState.pageSize,
    urlState.search,
    authContext.user,
    setViewState,
  ]);

  useEffect(() => {
    fetchAvailableEmployees();
    fetchAvailableProjects();
  }, [companyId, fetchAvailableEmployees, fetchAvailableProjects]);

  if (errorMessage) {
    return (
      <div
        className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
        role="alert"
      >
        {errorMessage}
      </div>
    );
  }

  if (presenceResponse === undefined) {
    return null;
  }

  const canCalculateWorkingTime = authContext.user!.roles!.some((role) =>
    [
      RoleEnum.KadrySpolki.toString(),
      RoleEnum.AdministratorSpolki.toString(),
      RoleEnum.AdministratorGlowny.toString(),
    ].includes(role),
  );

  const canShowCommissionHours =
    (authContext.user!.roles!.includes(RoleEnum.Kierownik) ||
      authContext.user!.isTimeManager) &&
    authContext.user!.companyId === 1;

  const legend = (
    <div
      className={clsx(
        "flex flex-wrap justify-center gap-4 md:gap-8 pt-4 text-sm mx-auto max-w-5xl",
        isMobile ? "text-xs" : "flex-col md:flex-row",
      )}
    >
      <div>
        <div className="font-semibold mb-2 text-gray-700 dark:text-gray-200">
          {t("presence.legend.workPlan")}
        </div>
        <div
          className={clsx(
            "gap-2",
            isMobile ? "flex flex-wrap" : "flex flex-col",
          )}
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#a3e635] rounded"></div>
            <div>{t("Holiday")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#4d7c0f] rounded"></div>
            <div>{t("Day off")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#1d4ed8] rounded"></div>
            <div>{t("Leave")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#ad4ae2] rounded"></div>
            <div>{t("holiday.HealthAbsence")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#4a5565] rounded"></div>
            <div>{t("Unconfirmed")}</div>
          </div>
        </div>
      </div>
      <div>
        <div className="font-semibold mb-2 text-gray-700 dark:text-gray-200">
          {t("presence.legend.presence")}
        </div>
        <div
          className={clsx(
            "gap-2",
            isMobile ? "flex flex-wrap" : "flex flex-col",
          )}
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-700 rounded"></div>
            <div>{t("presence.unconfirmedPresence")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <div>{t("presence.confirmedByManager")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-600 rounded"></div>
            <div>{t("presence.legend.confirmed")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-400 dark:bg-emerald-400 rounded"></div>
            <div>{t("presence.AddedInOptima")}</div>
          </div>
        </div>
      </div>
      <div>
        <div className="font-semibold mb-2 text-gray-700 dark:text-gray-200">
          {t("presence.legend.synchronization")}
        </div>
        <div
          className={clsx(
            "gap-2",
            isMobile ? "flex flex-wrap" : "flex flex-col",
          )}
        >
          <div className="flex items-center gap-2">
            <i className="dx-icon-close text-red-500"></i>
            <div>{t("Not synchronized")}</div>
          </div>
          <div className="flex items-center gap-2">
            <i className="dx-icon-clock text-orange-400"></i>
            <div>{t("Synchronizing")}</div>
          </div>
          <div className="flex items-center gap-2">
            <i className="dx-icon-check text-green-500"></i>
            <div>{t("Synchronized")}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActions = () => (
    <div
      className={clsx(
        "mb-4 flex gap-2",
        isMobile ? "flex-col" : "flex-wrap items-center gap-4",
      )}
    >
      <Button
        type="default"
        width={isMobile ? "100%" : undefined}
        text={t("presence.presencesToConfirm")}
        onClick={() => setShowPresenceConfirmPopup(true)}
      />
      {canCalculateWorkingTime ? (
        <Button
          type="default"
          width={isMobile ? "100%" : undefined}
          text={t("presence.calculateWorkingTimeReportsForChosenMonth")}
          onClick={handleCreateCalculateWorkingTimeSummaryJob}
        />
      ) : null}
      {canShowCommissionHours ? (
        <Button
          type="default"
          width={isMobile ? "100%" : undefined}
          text={t("presence.commissionHours")}
          onClick={() => setShowCommissionHoursPopup(true)}
        />
      ) : null}
    </div>
  );

  const renderMonthNav = () => (
    <div className="flex items-center gap-1">
      <Button
        icon="chevronprev"
        stylingMode="text"
        onClick={handlePreviousMonthButtonClick}
      />
      <div
        className={clsx(
          "text-center text-base",
          isMobile ? "flex-1 font-semibold" : "w-32",
        )}
      >
        {`${t(`monthsList.${urlState.month - 1}`)} ${urlState.year}`}
      </div>
      <Button
        icon="chevronnext"
        stylingMode="text"
        onClick={handleNextMonthButtonClick}
      />
    </div>
  );

  const renderSearch = () => (
    <TextBox
      value={urlState.search}
      placeholder={t("presence.search")}
      width={isMobile ? "100%" : undefined}
      onValueChange={(value) => setUrlState({ search: value, pageNumber: 1 })}
      showClearButton
    />
  );

  return (
    <>
      {isLoading ? (
        <LoadPanel
          shading
          shadingColor="rgba(0, 0, 0, 0.3)"
          visible
          message={t("common.dataFetching")}
        />
      ) : null}
      {isCalculateWorkingTimeSummaryProcessing ? (
        <LoadPanel
          shading
          shadingColor="rgba(0, 0, 0, 0.3)"
          visible
          message={t("presence.calculateWorkingTimeSummaryProcessing")}
        />
      ) : null}
      {isExporting ? (
        <LoadPanel
          shading
          shadingColor="rgba(0, 0, 0, 0.3)"
          visible
          message={t("presence.exportToXlsProcessing")}
        />
      ) : null}
      {showPresenceConfirmPopup ? (
        <PresenceConfirmPopup
          onClose={handlePresenceConfirmPopupClose}
          from={format(
            startOfMonth(new Date(urlState.year, urlState.month - 1, 1)),
            "yyyy-MM-dd",
          )}
          to={format(
            endOfMonth(new Date(urlState.year, urlState.month - 1, 1)),
            "yyyy-MM-dd",
          )}
        />
      ) : null}
      {presenceReportingPopupData ? (
        <PresenceReportingPopup
          availableEmployees={availableEmployees}
          availableProjects={availableProjects}
          companyId={presenceReportingPopupData.companyId}
          employeeId={presenceReportingPopupData.employeeId}
          entry={presenceReportingPopupData.entry}
          employeeEntries={presenceReportingPopupData.employeeEntries}
          canDeleteFullyConfirmed={isHrOrAdmin}
          absenceHourRange={presenceReportingPopupData.absenceHourRange}
          onClose={handlePresenceReportingPopupClose}
        />
      ) : null}
      {showCalculateWorkingTimeSummaryResultsDialog ? (
        <CalculateWorkingTimeSummaryResultDialog
          calculateWorkingTimeSummaryResults={
            calculateWorkingTimeSummaryResults
          }
          year={urlState.year}
          month={urlState.month}
          onClose={() => setShowCalculateWorkingTimeSummaryResultsDialog(false)}
        />
      ) : null}
      {showCommissionHoursPopup ? (
        <CommissionHoursV2Popup
          onClose={() => setShowCommissionHoursPopup(false)}
        />
      ) : null}

      {renderActions()}

      {isMobile ? (
        <div
          className={clsx(
            "sticky top-0 z-10 flex flex-col gap-2 pb-3",
            themeContext.theme === "dark" ? "bg-[#1e1e1e]" : "bg-white",
          )}
        >
          {renderMonthNav()}
          {renderSearch()}
          <Button
            icon="exportxlsx"
            text={t("presence.exportToXls")}
            disabled={isExporting}
            onClick={handleExportXlsxClick}
            width="100%"
          />
        </div>
      ) : (
        <Toolbar>
          <ToolbarItem location="before" locateInMenu="auto">
            {renderMonthNav()}
          </ToolbarItem>
          <ToolbarItem location="after" locateInMenu="auto">
            {renderSearch()}
          </ToolbarItem>
          <ToolbarItem location="after" locateInMenu="auto">
            <Button
              icon="exportxlsx"
              text={t("presence.exportToXls")}
              disabled={isExporting}
              onClick={handleExportXlsxClick}
            />
          </ToolbarItem>
        </Toolbar>
      )}

      {presenceResponse.data.length === 0 ? (
        <div
          className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
          role="alert"
        >
          {urlState.search !== ""
            ? t("common.noSearchResults")
            : t("common.noData")}
        </div>
      ) : isMobile ? (
        <>
          <div className="flex flex-col gap-3 pb-4">
            {presenceResponse.data.map((item) => {
              const expanded = expandedEmployeeId === item.employee.id;
              return (
                <div
                  key={item.employee.id}
                  className={clsx(
                    "rounded-lg border shadow-sm overflow-hidden",
                    themeContext.theme === "dark"
                      ? "bg-[#1e1e1e] border-gray-700"
                      : "bg-white border-gray-200",
                    item.employee.hasCivilContract
                      ? "border-[3px] border-[#e4f226]"
                      : "",
                  )}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedEmployeeId((prev) =>
                        prev === item.employee.id ? null : item.employee.id,
                      )
                    }
                    className={clsx(
                      "w-full text-left p-3 flex items-start gap-2",
                      themeContext.theme === "dark"
                        ? "hover:bg-[#2a2a2a]"
                        : "hover:bg-gray-50",
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate">
                        {`${item.employee.lastName} ${item.employee.firstName}`}
                      </div>
                      <div className="text-xs opacity-90 mt-1 break-words">
                        {`${t("presence.workPlanHoursAndDays")}: ${formatNumber(
                          item.summary.workPlan.timeInHours,
                          2,
                        )} ${t("godz.")} / ${item.summary.workPlan.days} ${t(
                          "dni",
                        )}`}
                      </div>
                      <div className="text-xs opacity-90 break-words">
                        {`${t("presence.presenceHoursAndDays")}: ${formatNumber(
                          item.summary.presence.timeInHours,
                          2,
                        )} ${t("godz.")} / ${item.summary.presence.days} ${t(
                          "dni",
                        )}`}
                      </div>
                      <div className="flex items-center gap-1 text-xs mt-1 break-words">
                        {item.workingHoursAcceptance ? (
                          <>
                            <i className="dx-icon-check text-green-500"></i>
                            <span>
                              {t(
                                "presence.workingHoursAcceptedAt",
                                acceptedAtParams(
                                  item.workingHoursAcceptance.acceptedAt,
                                ),
                              )}
                            </span>
                          </>
                        ) : (
                          <>
                            <i className="dx-icon-close text-red-500"></i>
                            <span>{t("presence.workingHoursNotAccepted")}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <i
                      className={clsx(
                        "dx-icon text-xl mt-1",
                        expanded ? "dx-icon-chevronup" : "dx-icon-chevrondown",
                      )}
                    />
                  </button>
                  {expanded ? (
                    <div className="flex flex-col gap-2 p-3 pt-0">
                      {item.entries.map((entry, index) => {
                        const meta = getCellMeta(
                          entry,
                          themeContext.theme === "dark",
                        );
                        const [entryCompanyId, entryEmployeeId] =
                          item.employee.id.split("-").map(Number);
                        const outsideContract = !isDayWithinEmployment(
                          employmentIndex.get(
                            employmentKey(entryCompanyId, entryEmployeeId),
                          ),
                          entry.date,
                        );
                        const disabled =
                          entry.isLent === true ||
                          entry.isBorrowed === false ||
                          outsideContract;
                        const cellTitle = outsideContract
                          ? t("presence.employeeNotEmployedForTheDay")
                          : entry.isLent
                            ? t("presence.employeeWasOnLoanForTheDay")
                            : entry.isBorrowed === false
                              ? t("presence.employeeWasNotOnBorrowForTheDay")
                              : undefined;
                        const dayLabel = _.upperFirst(
                          format(entry.date, "EEEE, d.LL", {
                            locale: i18n.language === "en" ? enUS : pl,
                          }),
                        );
                        return (
                          <div
                            key={`${item.employee.id}_${index}`}
                            className={clsx(
                              "flex flex-col rounded-lg border-2 overflow-hidden",
                              meta.bgColor,
                              meta.redBorder
                                ? "border-red-500"
                                : "border-transparent",
                              disabled
                                ? "opacity-60 cursor-not-allowed"
                                : "cursor-pointer",
                            )}
                            title={cellTitle}
                            onClick={
                              disabled
                                ? undefined
                                : () => handleEntryClick(item, entry)
                            }
                          >
                            {meta.hourlyAbsenceLabel ? (
                              <div className="w-full px-2 py-1 bg-blue-700 text-white text-[11px] font-semibold text-center">
                                {meta.hourlyAbsenceLabel}
                              </div>
                            ) : null}
                            <div className="flex items-stretch gap-3 p-3">
                              <div className="flex flex-col items-center justify-center min-w-[52px] font-bold">
                                <div className="text-[11px] uppercase opacity-80">
                                  {_.upperFirst(
                                    format(entry.date, "EEEEEE", {
                                      locale:
                                        i18n.language === "en" ? enUS : pl,
                                    }),
                                  )}
                                </div>
                                <div className="text-2xl leading-none mt-1">
                                  {format(entry.date, "d")}
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col justify-center text-sm min-w-0">
                                <div className="text-[11px] opacity-80">
                                  {dayLabel}
                                </div>
                                {meta.firstLine ? (
                                  <div className="font-medium break-words">
                                    {meta.firstLine}
                                  </div>
                                ) : null}
                                {meta.secondLine ? (
                                  <div className="text-xs opacity-90 break-words">
                                    {meta.secondLine}
                                  </div>
                                ) : null}
                                {entry.violations.length > 0 ? (
                                  <ul className="mt-1 text-xs text-white bg-red-600/90 rounded px-2 py-1 list-disc list-inside">
                                    {entry.violations.map((v, i) => (
                                      <li key={i}>{v}</li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-1 text-xl">
                                {entry.isLent || entry.isBorrowed ? (
                                  <i className="dx-icon-refresh"></i>
                                ) : null}
                                {meta.icon}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <Pagination
            showInfo
            showNavigationButtons
            showPageSizeSelector
            allowedPageSizes={[5, 10, 15, 20, 25]}
            pageIndex={Number(urlState.pageNumber)}
            pageSize={Number(urlState.pageSize)}
            itemCount={presenceResponse.pagination.totalRows}
            onPageIndexChange={(value) => setUrlState({ pageNumber: value })}
            onPageSizeChange={(value) =>
              setUrlState({ pageNumber: 1, pageSize: value })
            }
          />
          {legend}
        </>
      ) : (
        <>
          <div
            className={clsx(
              "table-wrapper",
              themeContext?.theme === "dark" ? "dark" : null,
            )}
            style={{ maxHeight: "60vh" }}
          >
            <table>
              <thead>
                <tr className="sticky">
                  <th className="sticky">
                    <div className="box-dark w-32 sm:w-72">
                      {t("Pracownik")}
                    </div>
                  </th>
                  {presenceResponse.data[0].entries.map((entry) => (
                    <th
                      key={entry.date}
                      style={{
                        backgroundColor:
                          themeContext?.theme === "dark"
                            ? "#1e1e1e"
                            : "#f9f9f9",
                      }}
                    >
                      <div className="box-dark" style={{ minWidth: 120 }}>
                        {_.upperFirst(
                          format(entry.date, "EEEEEE, d.LL", {
                            locale: i18n.language === "en" ? enUS : pl,
                          }),
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {presenceResponse.data.map((item, index) => {
                  return (
                    <tr key={`tr_${index}`}>
                      <td
                        key={`td_${index}`}
                        className="sticky"
                        style={{ height: "100px" }}
                      >
                        <div
                          className={clsx("box-dark w-32 sm:w-72 h-full", {
                            "box-dark border-[3px] border-[#e4f226] border-solid":
                              item.employee.hasCivilContract,
                          })}
                        >
                          <div className="font-bold text-wrap truncate">
                            {`${item.employee.lastName} ${item.employee.firstName}`}
                          </div>
                          <div className="text-wrap truncate">
                            {`${t(
                              "presence.workPlanHoursAndDays",
                            )}: ${formatNumber(
                              item.summary.workPlan.timeInHours,
                              2,
                            )} ${t("godz.")} / ${
                              item.summary.workPlan.days
                            } ${t("dni")}`}
                          </div>
                          <div className="text-wrap truncate">{`${t(
                            "presence.presenceHoursAndDays",
                          )}: ${formatNumber(
                            item.summary.presence.timeInHours,
                            2,
                          )} ${t("godz.")} / ${item.summary.presence.days} ${t(
                            "dni",
                          )}`}</div>
                          <div className="flex items-center gap-1 text-wrap truncate">
                            {item.workingHoursAcceptance ? (
                              <>
                                <i className="dx-icon-check text-green-500"></i>
                                <span>
                                  {t(
                                    "presence.workingHoursAcceptedAt",
                                    acceptedAtParams(
                                      item.workingHoursAcceptance.acceptedAt,
                                    ),
                                  )}
                                </span>
                              </>
                            ) : (
                              <>
                                <i className="dx-icon-close text-red-500"></i>
                                <span>
                                  {t("presence.workingHoursNotAccepted")}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                      {item.entries.map((entry, index) => {
                        const meta = getCellMeta(
                          entry,
                          themeContext.theme === "dark",
                        );
                        const [entryCompanyId, entryEmployeeId] =
                          item.employee.id.split("-").map(Number);
                        const outsideContract = !isDayWithinEmployment(
                          employmentIndex.get(
                            employmentKey(entryCompanyId, entryEmployeeId),
                          ),
                          entry.date,
                        );
                        const disabled =
                          entry.isLent === true ||
                          entry.isBorrowed === false ||
                          outsideContract;
                        const cellTitle = outsideContract
                          ? t("presence.employeeNotEmployedForTheDay")
                          : entry.isLent
                            ? t("presence.employeeWasOnLoanForTheDay")
                            : entry.isBorrowed === false
                              ? t("presence.employeeWasNotOnBorrowForTheDay")
                              : undefined;

                        return (
                          <td
                            className={clsx({
                              "cursor-not-allowed": disabled,
                              "opacity-60": outsideContract,
                            })}
                            title={cellTitle}
                            key={`td_${item.employee.id}_${index}`}
                            onClick={
                              disabled
                                ? undefined
                                : () => {
                                    const now = Date.now();
                                    const timeSinceLastClick =
                                      now - lastClickTime;

                                    if (timeSinceLastClick < 300) {
                                      handleEntryClick(item, entry);
                                    } else {
                                      setLastClickTime(now);
                                    }
                                  }
                            }
                          >
                            <div
                              id={`cell-${item.employee.id}-${formatDate(
                                entry.date,
                                "yyyyMMdd",
                              )}`}
                              className={clsx(
                                `min-h-[100px] flex flex-col ${meta.bgColor} rounded-md font-bold border-solid border-3 overflow-hidden`,
                                { "border-red-500": meta.redBorder },
                                { "border-transparent": !meta.redBorder },
                              )}
                            >
                              {meta.hourlyAbsenceLabel ? (
                                <div className="w-full px-1 py-0.5 bg-blue-700 text-white text-[10px] font-semibold text-center leading-tight">
                                  {meta.hourlyAbsenceLabel}
                                </div>
                              ) : null}
                              <div className="flex-1 flex flex-col justify-center items-center gap-1 p-1">
                                {meta.firstLine ? (
                                  <div>{meta.firstLine}</div>
                                ) : null}
                                {meta.secondLine ? (
                                  <div>{meta.secondLine}</div>
                                ) : null}
                                <div className="flex gap-1">
                                  {entry.isLent || entry.isBorrowed ? (
                                    <i className="dx-icon-refresh"></i>
                                  ) : null}
                                  {meta.icon}
                                </div>
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {presenceResponse.data.map((item) => {
            return item.entries
              .filter((e) => e.violations.length > 0)
              .map((e) => {
                return (
                  <Tooltip
                    key={`tt-${item.employee.id}-${e.date}`}
                    target={`#cell-${item.employee.id}-${formatDate(
                      e.date,
                      "yyyyMMdd",
                    )}`}
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    hideOnOutsideClick={false}
                    position="bottom"
                  >
                    {e.violations.map((v, i) => (
                      <div key={i}>{v}</div>
                    ))}
                  </Tooltip>
                );
              });
          })}
          <Pagination
            showInfo
            showNavigationButtons
            showPageSizeSelector
            allowedPageSizes={[5, 10, 15, 20, 25]}
            pageIndex={Number(urlState.pageNumber)}
            pageSize={Number(urlState.pageSize)}
            itemCount={presenceResponse.pagination.totalRows}
            onPageIndexChange={(value) => setUrlState({ pageNumber: value })}
            onPageSizeChange={(value) =>
              setUrlState({ pageNumber: 1, pageSize: value })
            }
          />
          {legend}
        </>
      )}
    </>
  );
};
